import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Modal, Select } from '@maxtropy/components';
import { Space, Spin } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2AreaCameraSelectPost,
  apiV2RobotMonitorAddPost,
  apiV2RobotMonitorDetailPost,
  apiV2RobotMonitorUpdatePost,
  apiV2SupervisoryAccountGetDeviceListByAccountPost,
  apiV2SupervisoryAccountListPost,
} from '@maxtropy/device-customer-apis-v2';

export enum InspectionRecorderAccessMode {
  PLATFORM = 1,
  THIRD_PARTY = 2,
}

export interface InspectionRecorderFormValues {
  accessMode: number;
  accountId?: number;
  accountName?: string;
  deviceId?: string;
  deviceName: string;
  weight: number;
}

interface EditModalProps {
  open: boolean;
  recorderId?: number;
  onCancel: () => void;
  onSuccess: (isCreate: boolean) => void;
}

const accessModeOptions = [
  { label: '平台', value: InspectionRecorderAccessMode.PLATFORM },
  { label: '第三方', value: InspectionRecorderAccessMode.THIRD_PARTY },
];

const EditModal: FC<EditModalProps> = ({ open, recorderId, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedAccessMode, setSelectedAccessMode] = useState<number>(InspectionRecorderAccessMode.PLATFORM);
  const [selectedAccountId, setSelectedAccountId] = useState<number>();

  const { data: accountPage } = useRequest(
    async () => {
      const res = await apiV2SupervisoryAccountListPost({});
      return res.list ?? [];
    },
    { ready: open }
  );

  const accountOptions = useMemo(() => {
    return (accountPage ?? [])
      .map(item => ({
        label: item.name || '',
        value: item.id!,
      }))
      .filter(item => item.label && item.value != null);
  }, [accountPage]);

  const { data: devicePage, loading: deviceLoading } = useRequest(
    async () => {
      if (!selectedAccountId) {
        return [];
      }
      const res = await apiV2SupervisoryAccountGetDeviceListByAccountPost({
        id: selectedAccountId,
      });
      return res.list ?? [];
    },
    {
      ready: open && selectedAccessMode === InspectionRecorderAccessMode.PLATFORM && !!selectedAccountId,
      refreshDeps: [selectedAccountId, selectedAccessMode, open],
    }
  );

  const platformDeviceOptions = useMemo(() => {
    return (devicePage ?? [])
      .map(item => ({
        label: item.supervisoryDeviceName || '',
        value: String(item.id ?? ''),
      }))
      .filter(item => item.label && item.value);
  }, [devicePage]);

  const { data: thirdPartyDeviceList, loading: thirdPartyDeviceLoading } = useRequest(
    async () => {
      const res = await apiV2AreaCameraSelectPost({ type: 2 });
      return res.list ?? [];
    },
    {
      ready: open && selectedAccessMode === InspectionRecorderAccessMode.THIRD_PARTY,
      refreshDeps: [selectedAccessMode, open],
    }
  );

  const thirdPartyDevices = useMemo(() => {
    return (thirdPartyDeviceList ?? []).flatMap(item => item.deviceList ?? []);
  }, [thirdPartyDeviceList]);

  const thirdPartyDeviceOptions = useMemo(() => {
    return thirdPartyDevices
      .map(item => ({
        label: item.deviceName || '',
        value: item.deviceId || '',
      }))
      .filter(item => item.label && item.value);
  }, [thirdPartyDevices]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSelectedAccessMode(InspectionRecorderAccessMode.PLATFORM);
      setSelectedAccountId(undefined);
      return;
    }

    if (!recorderId) {
      form.setFieldsValue({ accessMode: InspectionRecorderAccessMode.PLATFORM });
      return;
    }

    setDetailLoading(true);
    apiV2RobotMonitorDetailPost({ id: recorderId })
      .then(res => {
        const accessMode = Number(res.accessMode) || InspectionRecorderAccessMode.PLATFORM;
        const accountId = res.accountId ? Number(res.accountId) : undefined;
        setSelectedAccessMode(accessMode);
        setSelectedAccountId(accountId);
        form.setFieldsValue({
          accessMode,
          accountId,
          accountName: res.accountName,
          deviceId: res.deviceId,
          deviceName: res.deviceName,
          weight: res.weight,
        });
      })
      .finally(() => setDetailLoading(false));
  }, [form, open, recorderId]);

  const handleSubmit = async (values: InspectionRecorderFormValues) => {
    const payload = {
      accessMode: values.accessMode,
      accountId: values.accountId,
      accountName: values.accountName,
      deviceId: values.deviceId,
      deviceName: values.deviceName,
      weight: values.weight,
    };

    setSubmitLoading(true);

    if (recorderId) {
      apiV2RobotMonitorUpdatePost({
        id: recorderId,
        ...payload,
      })
        .then(() => {
          message.success('编辑成功');
          onSuccess(false);
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    } else {
      apiV2RobotMonitorAddPost(payload)
        .then(() => {
          message.success('新增成功');
          onSuccess(true);
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnClose
      contentClassName="modal-form-content"
      title={recorderId ? '编辑视频监控' : '新增视频监控'}
      footer={
        <Space size={16}>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" loading={submitLoading} onClick={() => form.submit()}>
            确认
          </Button>
        </Space>
      }
    >
      <Spin spinning={detailLoading}>
        <Form
          form={form}
          layout="horizontal"
          initialValues={{ accessMode: InspectionRecorderAccessMode.PLATFORM }}
          onFinish={handleSubmit}
        >
          <Form.Item name="accessMode" label="接入方式" rules={[{ required: true, message: '请选择接入方式' }]}>
            <Select
              options={accessModeOptions}
              placeholder="请选择"
              onChange={value => {
                const nextMode = Number(value);
                setSelectedAccessMode(nextMode);
                if (nextMode === InspectionRecorderAccessMode.THIRD_PARTY) {
                  setSelectedAccountId(undefined);
                  form.setFieldsValue({
                    accountId: undefined,
                    accountName: undefined,
                    deviceId: undefined,
                    deviceName: undefined,
                  });
                }
                if (nextMode === InspectionRecorderAccessMode.PLATFORM) {
                  form.setFieldsValue({
                    deviceId: undefined,
                    deviceName: undefined,
                  });
                }
              }}
            />
          </Form.Item>

          {selectedAccessMode === InspectionRecorderAccessMode.PLATFORM && (
            <>
              <Form.Item name="accountId" label="账户名称" rules={[{ required: true, message: '请选择账户名称' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="请选择"
                  options={accountOptions}
                  onChange={value => {
                    const nextAccountId = Number(value);
                    setSelectedAccountId(nextAccountId);
                    const account = accountPage?.find(item => item.id === nextAccountId);
                    form.setFieldsValue({
                      accountName: account?.name,
                      deviceId: undefined,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item name="deviceId" label="监控设备" rules={[{ required: true, message: '请选择监控设备' }]}>
                <Select
                  showSearch
                  placeholder="请选择"
                  loading={deviceLoading}
                  optionFilterProp="label"
                  options={platformDeviceOptions}
                  onChange={value => {
                    const device = devicePage?.find(item => String(item.id ?? '') === String(value));
                    form.setFieldsValue({
                      deviceName: device?.supervisoryDeviceName,
                    });
                  }}
                />
              </Form.Item>
            </>
          )}

          {selectedAccessMode === InspectionRecorderAccessMode.THIRD_PARTY && (
            <Form.Item name="deviceId" label="监控设备" rules={[{ required: true, message: '请选择监控设备' }]}>
              <Select
                showSearch
                placeholder="请选择"
                loading={thirdPartyDeviceLoading}
                optionFilterProp="label"
                options={thirdPartyDeviceOptions}
                onChange={value => {
                  const device = thirdPartyDevices.find(item => String(item.deviceId ?? '') === String(value));
                  form.setFieldsValue({
                    deviceName: device?.deviceName,
                  });
                }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="deviceName"
            label="监控设备名称"
            rules={[
              { required: true, message: '请输入监控设备名称' },
              { max: 50, message: '最多输入50个字符' },
            ]}
          >
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item name="weight" label="权重" rules={[{ required: true, message: '请输入权重' }]}>
            <InputNumber min={0} precision={0} max={9999} style={{ width: '100%' }} placeholder="请输入" />
          </Form.Item>
          <Form.Item name="accountName" hidden>
            <Input placeholder="请输入" />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditModal;
